import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ImageSlicePositions } from '~/containers/viewer/viewport/Viewport';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { Orientations } from '~/engine/view/views/PlaneView';
import { useSelectRegistration } from '~/hooks/registration/useRegistrationStore';
import {
    secondaryEnvironmentState,
    secondaryLowerSideViewState,
    secondaryMainViewState,
    secondaryUpperSideViewState,
    viewManagerState,
} from '~/state/atoms/engineAtoms';
import {
    areEnvironmentsLockedState,
    GridLayoutMode,
    gridLayoutModeState,
} from '~/state/atoms/viewerAtoms';
import {
    calculateNormalizedOrientationValue,
    getDominantAxisSign,
} from '~/utils/units';

type SecondaryEnvironmentRemovalOptions = {
    preserveSelection?: boolean;
    preserveLocks?: boolean;
};

export default function useViewportSecondaryEnvironment() {
    const viewManager = useRecoilValue(viewManagerState);
    const [secondaryEnvironment, setSecondaryEnvironment] = useRecoilState(
        secondaryEnvironmentState
    );
    const secondaryMain = useRecoilValue(secondaryMainViewState);
    const secondaryUpperSide = useRecoilValue(secondaryUpperSideViewState);
    const secondaryLowerSide = useRecoilValue(secondaryLowerSideViewState);
    const setGridLayoutMode = useSetRecoilState(gridLayoutModeState);
    const setAreEnvironmentsLocked = useSetRecoilState(
        areEnvironmentsLockedState
    );
    const selectRegistration = useSelectRegistration();

    const getOrCreateSecondaryEnvironment = () => {
        if (!viewManager) {
            return null;
        }
        if (!viewManager.secondaryEnvironment) {
            viewManager.enableSecondaryEnvironment();
        }
        return viewManager.secondaryEnvironment ?? null;
    };

    const setEnvironmentSecondaryImageSideBySide = (
        image: Image3D,
        registration: RigidRegistration
    ) => {
        const env = getOrCreateSecondaryEnvironment();
        if (!env) {
            return;
        }
        env.setImage(image);
        env.setRegistration(registration);
        setSecondaryEnvironment(env);
        selectRegistration(registration.sopInstanceUid, image.seriesUid);
        setGridLayoutMode(GridLayoutMode.SideBySide);
    };

    const removeEnvironmentSecondaryImageSideBySide = (
        options?: SecondaryEnvironmentRemovalOptions
    ) => {
        const { preserveSelection = false, preserveLocks = false } =
            options ?? {};
        if (!viewManager || !viewManager.secondaryEnvironment) {
            return;
        }
        viewManager.secondaryEnvironment.setImage(undefined);
        viewManager.secondaryEnvironment.setRegistration(undefined);
        viewManager.disableSecondaryEnvironment();
        setSecondaryEnvironment(null);
        if (!preserveSelection) {
            selectRegistration(undefined);
        }
        if (!preserveLocks) {
            setAreEnvironmentsLocked(false);
        }
        setGridLayoutMode(GridLayoutMode.Default);
    };

    const getSecEnvImagePositions = (
        sideBySideImageSliceOrientationPositions: ImageSlicePositions
    ): ImageSlicePositions => {
        if (
            !secondaryEnvironment ||
            !secondaryMain ||
            !secondaryUpperSide ||
            !secondaryLowerSide ||
            !secondaryEnvironment.image
        ) {
            return sideBySideImageSliceOrientationPositions;
        }

        const views = [secondaryMain, secondaryUpperSide, secondaryLowerSide];
        const updated: ImageSlicePositions = {
            ...sideBySideImageSliceOrientationPositions,
        };

        for (const orientation of Object.values(Orientations)) {
            const view = views.find((v) => v.label === orientation);
            if (view?.planeCoordinates === undefined) {
                return sideBySideImageSliceOrientationPositions;
            }

            const viewDirSign = getDominantAxisSign(view.viewDirection);
            updated[orientation] = calculateNormalizedOrientationValue(
                view.planeCoordinates,
                viewDirSign
            );
        }
        return updated;
    };

    return {
        setEnvironmentSecondaryImageSideBySide,
        removeEnvironmentSecondaryImageSideBySide,
        getSecEnvImagePositions,
    };
}
